import React, { useContext, useEffect, useRef, useState } from "react";
import {
  getEnvironment,
  isPdPlanType,
  logError,
  makePlanUrlPartForApi,
  PreviewContext,
  useCosts,
  useTranslate,
} from "../../../helpers";
import {
  ActionType,
  DrugInfo,
  Envs,
  Plan,
  PlanDetailsPageSection,
} from "../../../@types";
import {
  DrugCoverageSection,
  OverviewSection,
  BenefitsAndCostsSection,
  StarRatingsSection,
  OptionalPackagesSection,
  ExtraBenefitsSection,
  PlanHeader,
  WhatYoullPay,
} from "../";
import { StickyNav } from ".";
import { AppStore } from "../../../app/store";
import { getOtherDrugInfo } from "../../../api";
import { ApiError } from "../../../helpers/errors";

/**
 * A container whose only job is to derive `drugCostInfo` from Plan details.
 * Needed because a few components rely on cost information, `useCosts` involves
 * another API request, and the hook, `useCosts`, cannot be used conditionally
 * in the parent component (the only way it can be used there, AFAICT).
 * There could be better ways of doing this, but when
 * relying on the hook for drug cost info, this is what I came up with.
 * @param Props
 * @param Props.plan - Plan data
 * @returns - All of the page elements for plan details with both plan and drug
 *            cost data (as needed)
 */
const PlanDetailsCostsContainer: React.FC<{
  plan: Plan;
}> = ({ plan }) => {
  const [drugInfo, setDrugInfo] = useState<DrugInfo[]>([]);
  const t = useTranslate();
  const previewMode = useContext(PreviewContext);
  const {
    state: { prescriptions },
    dispatch,
  } = useContext(AppStore.AppContext);

  const fetchDrugInfo = async () => {
    const ndcs = prescriptions.map(p => p.ndc);

    if (ndcs.length) {
      const planUrl = makePlanUrlPartForApi(plan);
      try {
        const res = await getOtherDrugInfo(ndcs, planUrl);
        res && setDrugInfo(res);
      } catch (e) {
        logError(
          "Failed to get other drug information (OtherDrugInfo)",
          e as ApiError
        );
      }
    }
  };

  useEffect(() => {
    fetchDrugInfo();
  }, []);

  const isPdpPlan = isPdPlanType(plan.plan_type);
  const drugCostInfo = useCosts(plan);
  const hasOptionalPackages = plan.optional_benefits.length > 0;

  const overviewRef = useRef(null);
  const overviewSection = {
    section: PlanDetailsPageSection.OVERVIEW,
    label: t("plan_details.sticky_nav.overview"),
    ref: overviewRef,
  };
  const benefitsRef = useRef(null);
  const benefitsSection = {
    section: PlanDetailsPageSection.BENEFITS_AND_COSTS,
    label: t("plan_details.sticky_nav.benefits_and_costs"),
    ref: benefitsRef,
  };
  const drugCoverageRef = useRef(null);
  const drugCoverageSection = {
    section: PlanDetailsPageSection.DRUG_COVERAGE,
    label: t("plan_details.sticky_nav.drug_coverage"),
    ref: drugCoverageRef,
  };
  const extraBenefitsRef = useRef(null);
  const extraBenefitsSection = {
    section: PlanDetailsPageSection.EXTRA_BENEFITS,
    label: t("plan_details.sticky_nav.extra_benefits"),
    ref: extraBenefitsRef,
  };
  const optionalPackagesRef = useRef(null);
  const optionalPackagesSection = {
    section: PlanDetailsPageSection.OPTIONAL_PACKAGES,
    label: t("plan_details.sticky_nav.optional_packages"),
    ref: optionalPackagesRef,
  };
  const starRatingsRef = useRef(null);
  const starRatingsSection = {
    section: PlanDetailsPageSection.STAR_RATINGS,
    label: t("plan_details.sticky_nav.star_ratings"),
    ref: starRatingsRef,
  };
  const isIMPEnvironment = getEnvironment() === Envs.imp;
  let navItems = [];
  if (isPdpPlan) {
    navItems = [overviewSection, drugCoverageSection, starRatingsSection];
  } else {
    navItems = [
      overviewSection,
      benefitsSection,
      drugCoverageSection,
      extraBenefitsSection,
    ];
    hasOptionalPackages && navItems.push(optionalPackagesSection);
    navItems.push(starRatingsSection);
  }

  useEffect(() => {
    if (drugCostInfo.costs.length > 0) {
      const { costs: pharmacyCosts } = drugCostInfo;
      const mailOrderPharmacyCost = pharmacyCosts.find(
        pharmacy => pharmacy.npi === ""
      );
      if (mailOrderPharmacyCost) {
        dispatch({
          type: ActionType.SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS,
          payload: {
            preferred: mailOrderPharmacyCost.preferred,
            inNetwork: mailOrderPharmacyCost.in_network,
          },
        });
      }
    }
  }, [drugCostInfo]);

  return (
    <>
      <PlanHeader plan={plan} />
      <WhatYoullPay
        plan={plan}
        drugCostInfo={drugCostInfo}
        drugInfo={drugInfo}
      />
      {!previewMode && (
        <StickyNav items={navItems} drugCostInfo={drugCostInfo} />
      )}
      <OverviewSection plan={plan} sectionRef={overviewRef} />
      {!isPdpPlan && (
        <BenefitsAndCostsSection plan={plan} sectionRef={benefitsRef} />
      )}
      <DrugCoverageSection
        plan={plan}
        drugCostInfo={drugCostInfo}
        drugInfo={drugInfo}
        prescriptions={prescriptions || []}
        sectionRef={drugCoverageRef}
      />
      {!isPdpPlan && (
        <ExtraBenefitsSection plan={plan} sectionRef={extraBenefitsRef} />
      )}
      {!isPdpPlan && hasOptionalPackages && (
        <OptionalPackagesSection plan={plan} sectionRef={optionalPackagesRef} />
      )}
      {(!previewMode || isIMPEnvironment) && (
        <StarRatingsSection
          plan={plan}
          sectionRef={starRatingsRef}
          initialExpandAll={isIMPEnvironment}
        />
      )}
    </>
  );
};

export { PlanDetailsCostsContainer };
